import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Newspaper, Calendar, ExternalLink, Play, Award, Globe, Handshake, TrendingUp, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { ScrollReveal, StaggerContainer, StaggerItem } from "@/components/animations/ScrollAnimations";
import { SEO } from "@/components/SEO";

const pressReleases = [
  {
    date: "Oct 24, 2024",
    title: "EVGateway Expands EV Charging in Peru with Full Spanish Mobile App Interface",
    description: "Expanding our global footprint with localized solutions for Latin American EV drivers.",
    category: "Expansion",
    link: "https://www.linkedin.com/company/evgateway",
  },
  {
    date: "Oct 17, 2024",
    title: "Electrik Mobility Successfully Deploys Over 100 EV Charging Ports Across India",
    description: "Our white-label partner achieves major milestone in the Indian EV charging market.",
    category: "Partnership",
    link: "https://www.linkedin.com/company/evgateway",
  },
  {
    date: "Oct 10, 2024",
    title: "Foundation CCC Selects EVGateway for California University Charging Solutions",
    description: "Turnkey EV charging deployment across California community colleges and universities.",
    category: "Contract",
    link: "https://www.linkedin.com/company/evgateway",
  },
  {
    date: "Jun 17, 2024",
    title: "EVGateway Launches Advanced Fleet Solutions for Optimized Charger Management",
    description: "New fleet management features deliver 40% cost reduction for commercial operators.",
    category: "Product",
    link: "https://www.linkedin.com/company/evgateway",
  },
  {
    date: "Mar 15, 2024",
    title: "EVGateway Partners with NYSERDA to Improve NY EV Infrastructure",
    description: "Strategic partnership to accelerate EV charging deployment across New York State.",
    category: "Partnership",
    link: "https://www.governor.ny.gov/news/governor-hochul-announces-23-million-funding-and-awards-transportation-electrification",
  },
  {
    date: "Jan 20, 2024",
    title: "EVGateway Joins General Motors Ultium 360 Network",
    description: "Selected as a preferred charging network partner for GM's Ultium EV platform.",
    category: "Partnership",
    link: "https://news.gm.com/home.html",
  },
];

const milestones = [
  {
    year: "2025",
    title: "FedRAMP Authorization",
    description: "Achieved FedRAMP authorization for federal government deployments.",
    icon: Award,
  },
  {
    year: "2024",
    title: "Largest V2G Depot in Operation",
    description: "In Partnership with ZUM launched and supported largest Bi-directional V2G deployment.",
    icon: TrendingUp,
  },
  {
    year: "2023",
    title: "Inc. 5000 Recognition",
    description: "Named to Inc. 5000 list of fastest-growing private companies in America.",
    icon: Award,
  },
  {
    year: "2022",
    title: "Global Expansion",
    description: "Expanded operations to 13+ countries with localized solutions.",
    icon: Globe,
  },
];

const videos = [
  {
    title: "How to Charge Your EV in Grand Junction",
    source: "City of Grand Junction",
    embedId: "pqhnvLMbnC4",
    thumbnail: "https://img.youtube.com/vi/pqhnvLMbnC4/maxresdefault.jpg",
  },
  {
    title: "EV Chargers at Grant R. Brimhall & Newbury Park Libraries",
    source: "City of Thousand Oaks",
    embedId: "YpJafkf-P-c",
    thumbnail: "https://img.youtube.com/vi/YpJafkf-P-c/maxresdefault.jpg",
  },
];

const getCategoryColor = (category: string) => {
  switch (category) {
    case "Partnership":
      return "bg-primary/10 text-primary border-primary/20";
    case "Expansion":
      return "bg-accent/10 text-accent border-accent/20";
    case "Product":
      return "bg-green-500/10 text-green-500 border-green-500/20";
    case "Contract":
      return "bg-blue-500/10 text-blue-500 border-blue-500/20";
    default:
      return "bg-muted text-muted-foreground border-border";
  }
};

export default function News() {
  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="News & Media - EV Charging Industry Updates"
        description="Latest news, press releases, and media coverage from EVGateway. Stay updated on EV charging industry developments, partnerships, product launches, and company milestones."
        keywords="EVGateway news, EV charging news, press releases, EV industry updates, charging network news, electric vehicle news, EV partnerships"
        canonicalUrl="/news"
      />
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        {/* Ambient orbs */}
        <motion.div
          className="absolute top-1/4 right-[10%] w-[500px] h-[500px] rounded-full pointer-events-none"
          style={{ 
            background: 'radial-gradient(circle, hsl(var(--primary) / 0.12) 0%, transparent 70%)',
            filter: 'blur(60px)'
          }}
        />
        <motion.div
          className="absolute bottom-1/3 left-[5%] w-[400px] h-[400px] rounded-full pointer-events-none"
          style={{ 
            background: 'radial-gradient(circle, hsl(var(--accent) / 0.08) 0%, transparent 70%)',
            filter: 'blur(80px)'
          }}
        />
        
        <div className="container mx-auto px-4 relative z-10">
          <ScrollReveal>
            <div className="max-w-4xl mx-auto text-center">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background/80 backdrop-blur-sm border border-border/30 text-primary text-sm font-medium mb-6"
              >
                <Newspaper className="h-4 w-4" />
                Media & News
              </motion.div>

              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                Latest <span className="text-gradient">Updates</span>
              </h1>

              <p className="text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                Stay informed about EVGateway's latest developments, partnerships, and innovations in EV charging
                technology.
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Press Releases */}
      <section className="py-24 relative">
        <div className="container mx-auto px-4">
          <ScrollReveal>
            <div className="flex items-center justify-between mb-12">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold mb-2">
                  Press <span className="text-gradient">Releases</span>
                </h2>
                <p className="text-muted-foreground">Latest announcements and company news</p>
              </div>
            </div>
          </ScrollReveal>

          <StaggerContainer className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" staggerDelay={0.1}>
            {pressReleases.map((release, idx) => (
              <StaggerItem key={idx}>
                <motion.a
                  href={release.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ y: -5, scale: 1.02 }}
                  className="block bg-background/80 backdrop-blur-sm border border-border/30 rounded-3xl p-6 h-full hover:border-primary/40 hover:shadow-lg hover:shadow-primary/10 transition-all duration-300 group"
                >
                  <div className="flex items-center justify-between mb-4">
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium border ${getCategoryColor(release.category)}`}
                    >
                      {release.category}
                    </span>
                    <div className="flex items-center gap-2 text-muted-foreground text-sm">
                      <Calendar className="h-4 w-4" />
                      {release.date}
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold mb-3 group-hover:text-primary transition-colors">
                    {release.title}
                  </h3>
                  <p className="text-muted-foreground text-sm mb-4">{release.description}</p>
                  <div className="flex items-center gap-2 text-primary text-sm font-medium">
                    Read More
                    <ExternalLink className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </div>
                </motion.a>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-24 relative">
        <div className="container mx-auto px-4">
          <ScrollReveal>
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Featured <span className="text-gradient">Videos</span>
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                See EVGateway charging stations in action across various locations.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {videos.map((video, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="relative rounded-3xl overflow-hidden bg-background/80 backdrop-blur-sm border border-border/30 group"
              >
                <div className="aspect-video">
                  <iframe
                    width="100%"
                    height="100%"
                    src={`https://www.youtube.com/embed/${video.embedId}`}
                    title={video.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="rounded-t-3xl"
                  />
                </div>
                <div className="p-4">
                  <h3 className="font-semibold">{video.title}</h3>
                  <p className="text-sm text-muted-foreground">{video.source}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Milestones */}
      <section className="py-24 relative">
        <div className="container mx-auto px-4">
          <ScrollReveal>
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Key <span className="text-gradient">Milestones</span>
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Celebrating our journey of growth and innovation.
              </p>
            </div>
          </ScrollReveal>

          <StaggerContainer className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto" staggerDelay={0.1}>
            {milestones.map((milestone, idx) => (
              <StaggerItem key={idx}>
                <motion.div
                  whileHover={{ y: -5, scale: 1.02 }}
                  className={`bg-background/80 backdrop-blur-sm border border-border/30 rounded-3xl p-6 text-center h-full transition-all duration-300 hover:shadow-lg ${idx % 2 === 0 ? "hover:border-primary/40 hover:shadow-primary/10" : "hover:border-accent/40 hover:shadow-accent/10"}`}
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-4 ${idx % 2 === 0 ? "bg-primary/10" : "bg-accent/10"}`}>
                    <milestone.icon className={`h-6 w-6 ${idx % 2 === 0 ? "text-primary" : "text-accent"}`} />
                  </div>
                  <span className={`text-3xl font-bold ${idx % 2 === 0 ? "text-primary" : "text-accent"}`}>{milestone.year}</span>
                  <h3 className="font-semibold mt-2 mb-2">{milestone.title}</h3>
                  <p className="text-sm text-muted-foreground">{milestone.description}</p>
                </motion.div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-primary/5 to-accent/10" />
        <motion.div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full pointer-events-none"
          style={{ 
            background: 'radial-gradient(circle, hsl(var(--primary) / 0.15) 0%, transparent 70%)',
            filter: 'blur(80px)'
          }}
        />

        <div className="container mx-auto px-4 relative z-10">
          <ScrollReveal>
            <div className="max-w-3xl mx-auto text-center">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6">
                <Newspaper className="h-8 w-8 text-primary" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Media Inquiries?</h2>
              <p className="text-xl text-muted-foreground mb-8">
                For press inquiries, interviews, or media resources, please contact our communications team.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button variant="hero" size="lg" className="group" asChild>
                    <a href="mailto:press@evgateway.com">
                      Contact Press Team
                      <ArrowRight className="h-5 w-5 ml-2 transition-transform group-hover:translate-x-1" />
                    </a>
                  </Button>
                </motion.div>
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button
                    variant="outline"
                    size="lg"
                    className="border-foreground/30 text-foreground hover:bg-foreground/10 hover:border-foreground/50"
                    asChild
                  >
                    <a href="https://www.linkedin.com/company/evgateway" target="_blank" rel="noopener noreferrer">
                      Follow on LinkedIn
                    </a>
                  </Button>
                </motion.div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}